"use client"

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Menu, X, MessageCircle, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSmoothScroll } from '@/hooks/use-scroll-animation'

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const [isDesktopServicesOpen, setIsDesktopServicesOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const scrollTo = useSmoothScroll()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services', children: [
      { name: 'Breastfeeding', href: '/breastfeeding' },
      { name: 'Vaccinations', href: '/vaccinations' },
    ]},
    // { name: 'Resume', href: '/resume' },
    { name: 'Contact', href: '#contact' },
  ]

  const handleNavClick = (href: string) => {
    setIsOpen(false)
    setIsServicesOpen(false)
    setIsDesktopServicesOpen(false)
    if (href.startsWith('#')) {
      scrollTo(href.slice(1))
    }
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass shadow-lg py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <div className="relative w-10 h-10 rounded-full overflow-hidden flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <Image src="/images/logo.png" alt="Dr. Arushi Singhal Joshi - Pediatrician, Jodhpur" width={40} height={40} className="object-cover" priority fetchPriority="high" />
            </div>
            <div className="hidden sm:block">
              <p className="font-serif text-lg font-semibold text-navy">Dr. Arushi Singhal Joshi</p>
              <p className="text-xs text-muted-foreground -mt-1">Pediatrician & Lactation Consultant</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.children ? (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setIsDesktopServicesOpen(true)}
                  onMouseLeave={() => setIsDesktopServicesOpen(false)}
                >
                  <button
                    onClick={() => handleNavClick(link.href)}
                    onFocus={() => setIsDesktopServicesOpen(true)}
                    className="text-navy hover:text-teal transition-colors duration-300 font-medium relative flex items-center gap-1"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 text-navy/60 transition-all duration-200 ${isDesktopServicesOpen ? 'text-teal rotate-180' : ''}`} />
                    <span className={`absolute -bottom-1 left-0 h-0.5 bg-teal transition-all duration-300 ${isDesktopServicesOpen ? 'w-full' : 'w-0'}`} />
                  </button>
                  <div
                    className={`absolute top-full left-0 pt-2 min-w-[180px] transition-all duration-200 ${isDesktopServicesOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none translate-y-1'}`}
                  >
                    <div className="rounded-xl border border-slate-200/80 bg-white shadow-lg p-2">
                    {link.children.map((child) => (
                      <Link
                        key={child.name}
                        href={child.href}
                        onClick={() => setIsDesktopServicesOpen(false)}
                        className="block px-3 py-2 rounded-lg text-sm font-medium text-navy/90 hover:bg-teal/10 hover:text-teal transition-colors"
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                  </div>
                </div>
              ) : link.href.startsWith('#') ? (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link.href)}
                    className="text-navy hover:text-teal transition-colors duration-300 font-medium relative group"
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal transition-all duration-300 group-hover:w-full" />
                  </button>
                ) : (
                  <Link
                    key={link.name}
                    href={link.href}
                    className="text-navy hover:text-teal transition-colors duration-300 font-medium relative group"
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal transition-all duration-300 group-hover:w-full" />
                  </Link>
                )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href="https://wa.me/919136486156?text=Hello%20Dr.%20Arushi,%20I%20would%20like%20to%20book%20an%20appointment"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="bg-teal hover:bg-teal-dark text-white rounded-full px-6 transition-all duration-300 hover:shadow-lg hover:shadow-teal/30"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Schedule on WhatsApp
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-navy hover:text-teal transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 glass shadow-lg transition-all duration-500 overflow-hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-6 space-y-4">
          {navLinks.map((link) => (
            link.children ? (
              <div key={link.name} className="space-y-1">
                <button
                  onClick={() => {
                    handleNavClick(link.href)
                    setIsServicesOpen((v) => !v)
                  }}
                  className="w-full flex items-center justify-between text-navy hover:text-teal transition-colors duration-300 font-medium py-2"
                >
                  {link.name}
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                <div className={`${isServicesOpen ? 'block' : 'hidden'} pl-3 border-l border-slate-200 space-y-1`}>
                  {link.children.map((child) => (
                    <Link
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="block text-sm text-navy/80 hover:text-teal transition-colors duration-300 font-medium py-1.5"
                    >
                      {child.name}
                    </Link>
                  ))}
                </div>
              </div>
            ) : link.href.startsWith('#') ? (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.href)}
                className="block w-full text-left text-navy hover:text-teal transition-colors duration-300 font-medium py-2"
              >
                {link.name}
              </button>
            ) : (
              <Link
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block text-navy hover:text-teal transition-colors duration-300 font-medium py-2"
              >
                {link.name}
              </Link>
            )
          ))}
          <a
            href="https://wa.me/919136486156?text=Hello%20Dr.%20Arushi,%20I%20would%20like%20to%20book%20an%20appointment"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsOpen(false)}
          >
            <Button
              className="w-full bg-teal hover:bg-teal-dark text-white rounded-full"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Schedule on WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </nav>
  )
}
